import React, { useState } from 'react';
import { Trash2, SwitchCamera as UserSwitch } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
import { LinkStatus } from '../../../../types/pilgrimGuide';
import { Evidence } from '../../../../types/pilgrimGuide';
import { TransferGuideModal } from '../../modal/TransferGuideModal';
import { EvidenceModal } from '../../evidence/EvidenceModal';
import { EvidenceViewModal } from '../../evidence/EvidenceViewModal';

type Props = {
  status: LinkStatus;
  evidence?: Evidence;
  hasEvidence?: boolean;
  isOwner?: boolean;
  onSubmitEvidence?: () => void;
  onViewEvidence?: () => void;
  onEditEvidence?: () => void;
  onDelete?: () => void;
  onToggleOwnership?: () => void;
  isDeleting?: boolean;
  linkId: string;
  guideId: string;
  organizerId: string;
  organizerName: string;
  pilgrimId: string;
  pilgrimName: string;
  showTransfer?: boolean;
  disputeId?: string;
};

export const RowActions = ({ 
  status, 
  evidence, 
  hasEvidence,
  isOwner,
  onSubmitEvidence, 
  onViewEvidence,
  onEditEvidence,
  onDelete,
  onToggleOwnership,
  isDeleting = false,
  linkId,
  guideId,
  organizerId,
  organizerName,
  pilgrimId,
  pilgrimName,
  showTransfer = false,
  disputeId
}: Props) => {
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  return (
    <td className="px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <StatusBadge 
            status={status} 
            evidence={evidence}
            hasEvidence={hasEvidence}
            isOwner={isOwner}
            onSubmitEvidence={
              onSubmitEvidence ? () => setIsEvidenceModalOpen(true) : undefined
            }
            onViewEvidence={
              onViewEvidence ? () => setIsViewModalOpen(true) : undefined
            }
            onEditEvidence={
              onEditEvidence ? () => setIsEvidenceModalOpen(true) : undefined
            }
            onToggleOwnership={onToggleOwnership}
          />
        </div>
        
        <div className="flex items-center gap-2">
          {/* Transfer Button - Only hide for waiting identification */}
          {showTransfer && status !== 'waiting_identification' && (
            <button
              onClick={() => setIsTransferModalOpen(true)}
              className="p-2 text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
              title="Transfer to Another Guide"
            >
              <UserSwitch size={18} />
            </button>
          )}

          {/* Delete Button */}
          {onDelete && (
            <button
              onClick={onDelete}
              disabled={isDeleting}
              className={`p-2 rounded-lg transition-colors ${
                isDeleting 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-red-500 hover:bg-red-50'
              }`}
              title={isDeleting ? 'Deleting...' : 'Delete'}
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Modals */}
      {/* Transfer Modal */}
      <TransferGuideModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        linkId={linkId}
        currentGuideId={guideId}
        pilgrimId={pilgrimId}
        pilgrimName={pilgrimName}
      />

      {/* Evidence Modal */}
      {isEvidenceModalOpen && disputeId && (
        <EvidenceModal
          isOpen={true}
          onClose={() => setIsEvidenceModalOpen(false)}
          disputeId={disputeId}
          organizerId={organizerId}
          existingEvidence={evidence}
        />
      )}

      {/* View Evidence Modal */}
      {isViewModalOpen && evidence && (
        <EvidenceViewModal
          isOpen={true}
          onClose={() => setIsViewModalOpen(false)}
          guideName={organizerName}
          evidence={evidence}
        />
      )}
    </td>
  );
};
